import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Download, RefreshCw, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface IDataTableColumn<TData> {
  id?: string;
  accessorKey?: keyof TData | string;
  header?: string | React.ReactNode;
  cell?: (props: { row: { original: TData }; getValue: () => unknown }) => React.ReactNode;
  size?: number | string;
  minSize?: number;
  maxSize?: number;
  enableSorting?: boolean;
  enableHiding?: boolean;
  meta?: {
    className?: string;
    align?: 'left' | 'center' | 'right';
  };
  enableResizing?: boolean;
}

export interface IDataTableToolbarProps {
  onRefresh?: () => void;
  onExport?: () => void;
  isExport?: boolean;
  isShowSearch?: boolean;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  extraActions?: React.ReactNode;
}

export interface IDataTablePaginationProps {
  pageIndex: number;
  pageCount: number;
  pageSize: number;
  totalRecords?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizes?: number[];
}

export interface IDataTableProps<TData> {
  data: TData[];
  columns: IDataTableColumn<TData>[];
  pageIndex?: number;
  pageCount?: number;
  pageSize?: number;
  totalRecords?: number;
  isLoading?: boolean;
  enablePagination?: boolean;
  enableSorting?: boolean;
  enableColumnFilters?: boolean;
  enableGlobalFilter?: boolean;
  enableRowSelection?: boolean;
  enableExport?: boolean;
  exportTitle?: string;
  rowSelection?: Record<string, boolean>;
  onRowSelectionChange?: (selection: Record<string, boolean>) => void;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  onRefresh?: () => void;
  onRowClick?: (row: TData) => void;
  className?: string;
  height?: number | string;
  scrollStorageKey?: string;
  toolbar?: IDataTableToolbarProps;
  searchMode?: 'local' | 'remote';
  skeletonRows?: number;
  skeletonColumns?: number;
  noDataText?: string;
  getRowId?: (row: TData) => string;
}

export const DataTableToolbar = ({
  onRefresh,
  onExport,
  isExport,
  isShowSearch = true,
  searchPlaceholder = 'Tìm kiếm...',
  searchValue,
  onSearchChange,
  extraActions,
}: IDataTableToolbarProps) => {
  const [inputValue, setInputValue] = useState(searchValue || '');

  useEffect(() => {
    setInputValue(searchValue || '');
  }, [searchValue]);

  return (
    <div className='flex items-center justify-between gap-4 py-3'>
      <div className='flex items-center gap-2 flex-1'>
        {isShowSearch && (
          <div className="relative max-w-sm">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={searchPlaceholder}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onSearchChange?.(inputValue.trim());
                }
              }}
              className="h-10 w-[250px] rounded-xl border-border/70 bg-background/70 pl-9 pr-9"
            />
            {inputValue && onSearchChange && (
              <button
                type='button'
                onClick={() => {
                  setInputValue('');
                  onSearchChange('');
                }}
                aria-label='Xóa tìm kiếm'
                className='absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground'
              >
                <X className='h-3.5 w-3.5' />
              </button>
            )}
            <p className='mt-1 text-[11px] text-muted-foreground'>Nhấn Enter để tìm kiếm</p>
          </div>
        )}
        {extraActions}
      </div>
      <div className='flex items-center gap-2'>
        {inputValue && (
          <span className='hidden rounded-lg bg-primary/10 px-2 py-1 text-xs font-medium text-primary sm:inline-flex'>
            Đang lọc: "{inputValue}"
          </span>
        )}
        {isExport && onExport && (
          <Button variant="outline" size="sm" onClick={onExport}>
            <Download className="mr-2 h-4 w-4" /> Xuất dữ liệu
          </Button>
        )}
        {onRefresh && (
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw className="mr-2 h-4 w-4" /> Làm mới
          </Button>
        )}
      </div>
    </div>
  );
};

export const DataTablePagination = ({
  pageIndex,
  pageCount,
  pageSize,
  totalRecords,
  onPageChange,
  onPageSizeChange,
  pageSizes = [10, 20, 50, 100],
}: IDataTablePaginationProps) => {
  return (
    <div className='flex items-center justify-between border-t border-border/70 px-2 py-4'>
      <div className='flex items-center gap-3 text-sm text-muted-foreground'>
        <span>Tổng số: <span className="font-medium text-foreground">{totalRecords || 0}</span></span>
        <div className="flex items-center gap-2">
            <span>Hiển thị</span>
            <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
                <SelectTrigger className="h-8 w-[70px]">
                    <SelectValue placeholder={pageSize} />
                </SelectTrigger>
                <SelectContent side="top">
                    {pageSizes.map(size => (
                        <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
      </div>

      <div className='flex items-center gap-2'>
        <span className='text-sm text-muted-foreground mr-2'>Trang {pageIndex + 1} / {Math.max(pageCount, 1)}</span>
        <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-8 w-8 rounded-lg p-0" onClick={() => onPageChange(0)} disabled={pageIndex <= 0}>{'<<'}</Button>
            <Button variant="outline" size="sm" className="h-8 w-8 rounded-lg p-0" onClick={() => onPageChange(pageIndex - 1)} disabled={pageIndex <= 0}>{'<'}</Button>
            <Button variant="outline" size="sm" className="h-8 w-8 rounded-lg p-0" onClick={() => onPageChange(pageIndex + 1)} disabled={pageIndex >= pageCount - 1}>{'>'}</Button>
            <Button variant="outline" size="sm" className="h-8 w-8 rounded-lg p-0" onClick={() => onPageChange(Math.max(pageCount - 1, 0))} disabled={pageIndex >= pageCount - 1}>{'>>'}</Button>
        </div>
      </div>
    </div>
  );
};

const DataTableSkeleton = ({ rows = 5, columns = 5 }: { rows?: number; columns?: number }) => {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <TableRow key={`skeleton-row-${rowIndex}`}>
          {Array.from({ length: columns }).map((__, colIndex) => (
            <TableCell key={`skeleton-cell-${rowIndex}-${colIndex}`}>
                <Skeleton className="h-5 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
};

export function DataTable<TData extends Record<string, unknown>>({
  data,
  columns,
  pageIndex = 0,
  pageCount = 1,
  pageSize = 50,
  totalRecords,
  isLoading,
  enablePagination = true,
  enableGlobalFilter = true,
  enableRowSelection = false,
  enableExport = false,
  exportTitle = 'DataTable',
  rowSelection = {},
  onRowSelectionChange,
  onPageChange,
  onPageSizeChange,
  onRefresh,
  onRowClick,
  className,
  height,
  scrollStorageKey,
  toolbar,
  searchMode = 'local',
  skeletonRows = 10,
  skeletonColumns = 6,
  noDataText = 'Không có dữ liệu',
  getRowId,
}: IDataTableProps<TData>) {
  const safeData = useMemo(() => {
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object') return Object.values(data) as TData[];
    return [];
  }, [data]);

  const safeColumns = useMemo(() => (Array.isArray(columns) ? columns : []), [columns]);
  const [globalFilter, setGlobalFilter] = useState('');
  const activeSearchValue = toolbar?.searchValue ?? globalFilter;
  const handleSearchChange = toolbar?.onSearchChange ?? setGlobalFilter;
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!scrollStorageKey || !scrollContainerRef.current) return;
    const savedScrollTop = Number.parseInt(sessionStorage.getItem(scrollStorageKey) || '0', 10);
    if (Number.isFinite(savedScrollTop) && savedScrollTop > 0) {
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = savedScrollTop;
        }
      });
    }
  }, [scrollStorageKey]);

  const displayedData = useMemo(() => {
    if (searchMode === 'remote' || !enableGlobalFilter || !activeSearchValue.trim()) return safeData;
    const keyword = activeSearchValue.toLowerCase();
    return safeData.filter((row) => {
      return safeColumns.some((col) => {
        if (!col.accessorKey) return false;
        const value = (row as any)[String(col.accessorKey)];
        return String(value ?? '').toLowerCase().includes(keyword);
      });
    });
  }, [safeData, safeColumns, enableGlobalFilter, activeSearchValue, searchMode]);

  const resolveRowId = (row: TData, index: number): string => {
    if (getRowId) return getRowId(row);
    const id = (row as any).id;
    return id !== undefined && id !== null ? String(id) : String(index);
  };

  const handleToggleRow = (row: TData, index: number, checked: boolean) => {
    if (!onRowSelectionChange) return;
    const rowId = resolveRowId(row, index);
    onRowSelectionChange({ ...rowSelection, [rowId]: checked });
  };

  const handleToggleAll = (checked: boolean) => {
    if (!onRowSelectionChange) return;
    if (!checked) {
      onRowSelectionChange({});
      return;
    }
    const next: Record<string, boolean> = {};
    displayedData.forEach((row, index) => {
      next[resolveRowId(row, index)] = true;
    });
    onRowSelectionChange(next);
  };

  const handleExport = () => {
    const rows = displayedData.map((row) => {
      const out: Record<string, unknown> = {};
      safeColumns.forEach((col, idx) => {
        if (!col.accessorKey) return;
        const header = typeof col.header === 'string' ? col.header : String(col.id || col.accessorKey || `col_${idx}`);
        out[header] = (row as any)[String(col.accessorKey)];
      });
      return out;
    });
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'DATA');
    XLSX.writeFile(workbook, `${exportTitle}.xlsx`);
  };

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <DataTableToolbar
        onRefresh={onRefresh}
        onExport={enableExport ? handleExport : undefined}
        isExport={enableExport}
        isShowSearch={toolbar?.isShowSearch ?? enableGlobalFilter}
        searchPlaceholder={toolbar?.searchPlaceholder || 'Tìm kiếm...'}
        searchValue={activeSearchValue}
        onSearchChange={handleSearchChange}
        extraActions={toolbar?.extraActions}
      />

      <div
        ref={scrollContainerRef}
        className='surface overflow-auto ring-1 ring-border/40'
        style={{ maxHeight: height || 'auto' }}
        onScroll={(event) => {
          if (!scrollStorageKey) return;
          sessionStorage.setItem(scrollStorageKey, String(event.currentTarget.scrollTop));
        }}
      >
        <Table>
          <TableHeader>
            <TableRow className='bg-muted/30'>
              {enableRowSelection && (
                <TableHead className="w-[40px] text-center">
                  <Checkbox
                    checked={displayedData.length > 0 && displayedData.every((row, index) => !!rowSelection[resolveRowId(row, index)])}
                    onCheckedChange={(checked) => handleToggleAll(checked === true)}
                  />
                </TableHead>
              )}
              {safeColumns.map((col, index) => (
                <TableHead 
                    key={`${String(col.id || col.accessorKey || 'col')}-${index}`} 
                    style={{ width: typeof col.size === 'number' ? col.size : undefined }}
                  className={cn('sticky top-0 z-10 bg-muted/70 text-xs uppercase tracking-wider text-muted-foreground backdrop-blur', col.meta?.align === 'center' && 'text-center', col.meta?.align === 'right' && 'text-right')}
                >
                  {typeof col.header === 'string' ? col.header : String(col.id || col.accessorKey || '')}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <DataTableSkeleton rows={skeletonRows} columns={Math.max(safeColumns.length, skeletonColumns) + (enableRowSelection ? 1 : 0)} />
            ) : displayedData.length > 0 ? (
              displayedData.map((row, rowIndex) => {
                const rowId = resolveRowId(row, rowIndex);
                const selected = !!rowSelection[rowId];
                return (
                  <TableRow 
                    key={rowId} 
                    className={cn('transition-colors odd:bg-transparent even:bg-muted/10', onRowClick && 'cursor-pointer hover:bg-primary/5')} 
                    onClick={() => onRowClick?.(row)}
                    data-state={selected && "selected"}
                  >
                    {enableRowSelection && (
                      <TableCell onClick={(e) => e.stopPropagation()} className="w-[40px] text-center">
                        <Checkbox
                          checked={selected}
                          onCheckedChange={(checked) => handleToggleRow(row, rowIndex, checked === true)}
                        />
                      </TableCell>
                    )}
                    {safeColumns.map((col, colIndex) => {
                      const rawValue = col.accessorKey ? (row as any)[String(col.accessorKey)] : undefined;
                      return (
                        <TableCell
                          key={`${String(col.id || col.accessorKey || 'cell')}-${colIndex}`}
                          className={cn(
                              col.meta?.className,
                              col.meta?.align === 'center' && 'text-center', 
                              col.meta?.align === 'right' && 'text-right'
                          )}
                        >
                          {col.cell
                            ? col.cell({ row: { original: row }, getValue: () => rawValue })
                            : String(rawValue ?? '')}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={safeColumns.length + (enableRowSelection ? 1 : 0)} className='h-24 text-center'>
                  {noDataText}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {enablePagination && (
        <DataTablePagination
          pageIndex={pageIndex}
          pageCount={pageCount}
          pageSize={pageSize}
          totalRecords={totalRecords ?? displayedData.length}
          onPageChange={(page) => onPageChange?.(page)}
          onPageSizeChange={(size) => onPageSizeChange?.(size)}
        />
      )}
    </div>
  );
}

export default DataTable;
